import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { ArrowRight, Loader2, Store } from "lucide-react";
import { memo } from "react";
import { UnmappedItemsWarning } from "./UnmappedItemsWarning";

type UnmappedItem = SalonStockerHttpTypes.StoreExternalCheckoutUnmappedItem;

export interface ExternalCheckoutDialogViewProps {
  /** Whether the interstitial is open. */
  open: boolean;
  /** Open-state change callback. */
  onOpenChange: (open: boolean) => void;
  /** The supplier's display name. */
  sellerName: string;
  /**
   * The supplier's platform label (e.g. "shopify"); undefined while loading.
   * Falls back to "secure" in the copy.
   */
  platform?: string;
  /** Whether the checkout URL is still being fetched. */
  isLoading: boolean;
  /** A user-facing error message, or null when there is no error. */
  errorMessage: string | null;
  /** Whether a checkout URL is ready (gates the Continue button). */
  ready: boolean;
  /** Items that won't transfer to the supplier's store; empty when all mapped. */
  unmapped: UnmappedItem[];
  /** Whether the salon acknowledged the dropped items. */
  acknowledged: boolean;
  /** Acknowledgement toggle callback. */
  onAcknowledgedChange: (acknowledged: boolean) => void;
  /** Confirm handler - the container opens the supplier's store in a new tab. */
  onConfirm: () => void;
}

/** Title-cases a platform label ("shopify" -> "Shopify") for display. */
function formatPlatform(platform: string | undefined): string {
  if (!platform) return "secure";
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

/**
 * Presentational interstitial for the external-checkout handoff. Pure and
 * fully driven by props (no data fetching), so every state - loading, ready,
 * items-won't-transfer, and error - is storyable and testable. The container
 * ({@link ExternalCheckoutDialog}) owns the mutation and opens the supplier's
 * store in a new tab.
 */
export const ExternalCheckoutDialogView = memo(
  function ExternalCheckoutDialogView({
    open,
    onOpenChange,
    sellerName,
    platform,
    isLoading,
    errorMessage,
    ready,
    unmapped,
    acknowledged,
    onAcknowledgedChange,
    onConfirm
  }: ExternalCheckoutDialogViewProps) {
    const platformLabel = formatPlatform(platform);
    const needsAcknowledgement = unmapped.length > 0;
    const canContinue = ready && (!needsAcknowledgement || acknowledged);

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="h-5 w-5 text-purple-600" aria-hidden="true" />
              Continue to {sellerName}
            </DialogTitle>
            <DialogDescription>
              We&apos;ll open {sellerName}&apos;s {platformLabel} checkout in a
              new tab so you can place this order directly with them.
            </DialogDescription>
          </DialogHeader>

          {isLoading && (
            <div
              className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500"
              role="status"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Preparing your order…
            </div>
          )}

          {errorMessage && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              role="alert"
            >
              {errorMessage}
            </div>
          )}

          {needsAcknowledgement && (
            <UnmappedItemsWarning
              items={unmapped}
              acknowledged={acknowledged}
              onAcknowledgedChange={onAcknowledgedChange}
            />
          )}

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              aria-label="Cancel and stay on salonstocker"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={!canContinue}
              aria-label={`Continue to ${sellerName}'s checkout`}
            >
              Continue to {platformLabel} checkout
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);
